import { useEffect, useState } from 'react';
import { Tooltip } from '@mui/material';
import { useDispatch } from 'react-redux';
import { unwrapResult } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';

import { Badge, CustomAlert, CustomButton, Text } from '../../shared/uiComponents';
import { errorNormalizer } from '../../shared/Helpers/functions';
import { Info } from '../../shared/Assets/svgIcons';
import { Loader, InfoRow as Row } from '../StyledComponents';
import { AdminTypes } from '../Menu';

import UserInfo from './userInfo';
import InsuranceList from './insuranceList';
import ClientsList from './clientList';
import SupervisorList from './supervisorsList';
import BCBAList from './bcbaList';

import { DispatchProperties, useSelector } from '../../redux/store';
import { getUserAccountDetails, resendVerificationEmail } from '../../redux/State/identitySlice/userSlice';
import { UserProperties } from '../../redux/API/userAPIProperties';
import NonBillableHours from './nonBillableHours';

const Content = ({ onClose, item }: { item: UserProperties; onClose: () => void }) => {
	const dispatch = useDispatch<DispatchProperties>();

	const accountInfo = useSelector((state) => state.identityUser.accountInfo);
	const loading = useSelector((state) => state.identityUser.loadingAccountInfo);

	const resendVerificationHandler = () => {
		if (!item.id) return;
		dispatch(resendVerificationEmail(item.id))
			.then(unwrapResult)
			.then(() => toast('Success'))
			.catch(errorNormalizer)
			.finally(() => onClose());
	};

	useEffect(() => {
		if (!item.id) return;
		dispatch(getUserAccountDetails(item.id));
	}, [item.id, dispatch]);

	return loading ? (
		<Loader />
	) : (
		<>
			<Row>
				<Text title="Email confirmed:" type="psm" fontWeight="semiBold" />
				<div>
					<Badge
						label={accountInfo.emailConfirmed ? 'Confirmed' : 'Pending'}
						sizeType="sm"
						type={accountInfo.emailConfirmed ? 'solidSuccess' : 'solidPrimary'}
					/>
				</div>
			</Row>
			{[AdminTypes.cs, AdminTypes.admin, AdminTypes.bcba, AdminTypes.bt].includes(item.role.section.id) && <NonBillableHours userId={item.id} />}
			<UserInfo accountInfo={accountInfo} userInfo={item} />
			<InsuranceList id={item.id} />
			<ClientsList item={item} />
			{item.role.section.id === AdminTypes.bcba && <SupervisorList userId={item.id} />}
			{item.role.section.id === AdminTypes.cs && <BCBAList userId={item.id} />}
			<CustomButton
				title="Resend Verification"
				onClick={resendVerificationHandler}
				disabled={accountInfo.emailConfirmed}
			/>
		</>
	);
};

const AccountDetails = ({
	item,
	showMoreInfoButton = false,
}: {
	item: UserProperties;
	showMoreInfoButton?: boolean;
}) => {
	const [infoOpen, setInfoOpen] = useState<boolean>(false);

	const showUserInfo = () => {
		setInfoOpen(true);
	};

	return (
		<>
			{showMoreInfoButton ? (
				<CustomButton
					title="View More"
					onClick={showUserInfo}
					sizeType="sm"
					buttonType="secondary"
					style={{ minWidth: '100%' }}
				/>
			) : (
				<div onClick={showUserInfo} className="marginRight16" style={{ cursor: 'pointer' }}>
					<Tooltip title="More Information">
						<span>
							<Info />
						</span>
					</Tooltip>
				</div>
			)}
			<CustomAlert
				open={infoOpen}
				onClose={() => setInfoOpen(false)}
				title="Account Details"
				Content={({ onClose }) => <Content item={item} onClose={onClose} />}
			/>
		</>
	);
};

export default AccountDetails;
